import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraFormField, SpecraInput, SpecraSelect, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { VehicleRelationship } from '../../../../../../gen/ts/mono/datagateway/v1/vehicle_identity_pb';
import { identityLabel } from './Contracts/facts';
import { dateInput, dateValue, vehicleSaveError, vehicleTypeLabel } from './contractVehicleUi';
import { VehicleProfileEditor } from './VehicleProfileEditor';
import { useVehicleBrowse, useVehicleCompanies, useVehicleHolderContracts, useVehicleWrites } from './vehicleIdentityHooks';
export const relationshipLabel = (value)=>({
        direct_holder: 'Direct holder',
        jv_participant: 'JV participant',
        other: 'Other',
        team_member: 'Legacy team member'
    })[value] ?? value;
export function VehicleRelationshipEditor({ relationship, vehicleId = '', companyId = '', onDone }) {
    const [draft, setDraft] = useState(()=>relationship?.clone() ?? new VehicleRelationship({
            id: crypto.randomUUID(),
            vehicleId,
            companyId,
            relationshipType: 'direct_holder',
            verificationStatus: 'UNVERIFIED'
        }));
    const [search, setSearch] = useState('');
    const [creatingVehicle, setCreatingVehicle] = useState(false);
    const [contractSearch, setContractSearch] = useState('');
    const [validation, setValidation] = useState('');
    const [blocked, setBlocked] = useState(false);
    const companies = useVehicleCompanies();
    const vehicles = useVehicleBrowse({
        query: search
    });
    const contracts = useVehicleHolderContracts(contractSearch);
    const writes = useVehicleWrites();
    const location = useLocation();
    const origin = location.pathname + location.search;
    const set = (changes)=>setDraft(new VehicleRelationship({
            ...draft,
            ...changes
        }));
    const options = (companies.data ?? []).map((c)=>({
            id: c.id,
            label: `${c.name} · UEI ${c.uei || 'unknown'} · CAGE ${c.cageCode || 'unknown'} · ${c.id}`
        }));
    const holderContracts = (contracts.data ?? []).filter((c)=>c.identity?.kind === 'holder_contract' && c.refs?.vehicleId === draft.vehicleId && c.refs?.companyId === draft.holderCompanyId);
    if (creatingVehicle) return /*#__PURE__*/ _jsx(VehicleProfileEditor, {
        onCancel: ()=>setCreatingVehicle(false),
        onSaved: (p)=>{
            if (p.vehicle) set({
                vehicleId: p.vehicle.id,
                holderRegistryContractId: ''
            });
            setCreatingVehicle(false);
        }
    });
    return /*#__PURE__*/ _jsxs("form", {
        className: "space-y-3 rounded border p-3",
        "data-testid": Testids.vehicleIdentity.relationshipForm,
        onSubmit: async (e)=>{
            e.preventDefault();
            setValidation('');
            if (!draft.vehicleId || !draft.companyId || !draft.holderCompanyId || !draft.sourceBasis.trim()) {
                setValidation('Select actual company, vehicle and holder identities and record the source / basis.');
                return;
            }
            if (draft.relationshipType === 'direct_holder' && draft.companyId !== draft.holderCompanyId) {
                setValidation('A direct holder must be the actual holding company. For a JV participant, select that relationship and its actual holder.');
                return;
            }
            if (draft.verificationStatus === 'VERIFIED' && !draft.verifiedFact.trim()) {
                setValidation('Describe the verified fact and its supported period; verification is not eligibility.');
                return;
            }
            try {
                await writes.relationship.mutateAsync(draft);
                onDone();
            } catch (error) {
                setBlocked(error instanceof ConnectError && [
                    Code.Aborted,
                    Code.AlreadyExists
                ].includes(error.code));
            }
        },
        children: [
            /*#__PURE__*/ _jsx("h3", {
                children: relationship ? 'Edit recorded relationship' : 'Record explicit relationship'
            }),
            !vehicleId && !relationship && /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Find Vehicle by name or alias",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    "aria-label": "Find Vehicle by name or alias",
                    value: search,
                    onChange: (e)=>setSearch(e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Vehicle mechanism",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Vehicle mechanism",
                    "data-testid": Testids.vehicleIdentity.vehiclePicker,
                    value: draft.vehicleId,
                    disabled: !!relationship || !!vehicleId,
                    onChange: (e)=>set({
                            vehicleId: e.target.value,
                            holderRegistryContractId: ''
                        }),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Select active mechanism"
                        }),
                        draft.vehicleId && !vehicles.data?.some((v)=>v.id === draft.vehicleId) && /*#__PURE__*/ _jsxs("option", {
                            value: draft.vehicleId,
                            children: [
                                "Existing mechanism · ",
                                draft.vehicleId
                            ]
                        }),
                        (vehicles.data ?? []).map((v)=>/*#__PURE__*/ _jsxs("option", {
                                value: v.id,
                                children: [
                                    v.name,
                                    " · ",
                                    vehicleTypeLabel(v.vehicleType),
                                    " · agency",
                                    ' ',
                                    v.awardingAgencyOrgId || 'unresolved',
                                    " · ",
                                    v.id
                                ]
                            }, v.id))
                    ]
                })
            }),
            !vehicleId && !relationship && /*#__PURE__*/ _jsx(SpecraButton, {
                type: "button",
                variant: "secondary",
                onClick: ()=>setCreatingVehicle(true),
                children: "Create missing Vehicle and return here"
            }),
            vehicles.isError && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Vehicle choices unavailable."
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Company",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Company",
                    "data-testid": Testids.vehicleIdentity.companyPicker,
                    value: draft.companyId,
                    disabled: !!companyId || !!relationship,
                    onChange: (e)=>set({
                            companyId: e.target.value
                        }),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Select actual company"
                        }),
                        draft.companyId && !options.some((c)=>c.id === draft.companyId) && /*#__PURE__*/ _jsxs("option", {
                            value: draft.companyId,
                            children: [
                                "Existing company · ",
                                draft.companyId
                            ]
                        }),
                        options.map((c)=>/*#__PURE__*/ _jsx("option", {
                                value: c.id,
                                children: c.label
                            }, c.id))
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Relationship",
                children: /*#__PURE__*/ _jsx(SpecraSelect, {
                    "aria-label": "Relationship",
                    value: draft.relationshipType,
                    onChange: (e)=>set({
                            relationshipType: e.target.value
                        }),
                    children: [
                        'direct_holder',
                        'jv_participant',
                        'other',
                        ...draft.relationshipType === 'team_member' ? [
                            'team_member'
                        ] : []
                    ].map((t)=>/*#__PURE__*/ _jsx("option", {
                            value: t,
                            children: relationshipLabel(t)
                        }, t))
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Actual holder / intermediary",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Actual holder / intermediary",
                    "data-testid": Testids.vehicleIdentity.holderPicker,
                    value: draft.holderCompanyId,
                    onChange: (e)=>set({
                            holderCompanyId: e.target.value,
                            holderRegistryContractId: ''
                        }),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Select actual legal holder"
                        }),
                        draft.holderCompanyId && !options.some((c)=>c.id === draft.holderCompanyId) && /*#__PURE__*/ _jsxs("option", {
                            value: draft.holderCompanyId,
                            children: [
                                "Existing holder · ",
                                draft.holderCompanyId
                            ]
                        }),
                        options.map((c)=>/*#__PURE__*/ _jsx("option", {
                                value: c.id,
                                children: c.label
                            }, c.id))
                    ]
                })
            }),
            companies.isLoading && /*#__PURE__*/ _jsx("output", {
                children: "Loading company identities…"
            }),
            companies.isError && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Company identities unavailable. No identity has been inferred."
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Find contextual holder contract by PIID",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    "aria-label": "Find contextual holder contract by PIID",
                    value: contractSearch,
                    onChange: (e)=>setContractSearch(e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Holder contract (not task order)",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Holder contract (not task order)",
                    "data-testid": Testids.vehicleIdentity.holderContract,
                    value: draft.holderRegistryContractId,
                    onChange: (e)=>{
                        const c = holderContracts.find((c)=>c.id === e.target.value);
                        set({
                            holderRegistryContractId: e.target.value,
                            ...c ? {
                                holderContractNumber: c.identity?.piid || ''
                            } : {}
                        });
                    },
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "",
                            children: "Unresolved / source PIID only"
                        }),
                        draft.holderRegistryContractId && !holderContracts.some((c)=>c.id === draft.holderRegistryContractId) && /*#__PURE__*/ _jsxs("option", {
                            value: draft.holderRegistryContractId,
                            children: [
                                "Existing contextual contract · ",
                                draft.holderRegistryContractId
                            ]
                        }),
                        holderContracts.map((c)=>/*#__PURE__*/ _jsxs("option", {
                                value: c.id,
                                children: [
                                    identityLabel(c.identity),
                                    " · parent record ",
                                    c.refs?.parentId || 'unknown'
                                ]
                            }, c.id))
                    ]
                })
            }),
            contracts.isFetching && /*#__PURE__*/ _jsx("output", {
                children: "Loading contextual contracts…"
            }),
            contracts.isError && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Holder contracts unavailable; retain unresolved source PIID."
            }),
            draft.holderRegistryContractId && /*#__PURE__*/ _jsx(Link, {
                className: "text-primary underline",
                to: `/registries?tab=contracts&record=${encodeURIComponent(draft.holderRegistryContractId)}&returnTo=${encodeURIComponent(origin)}`,
                children: "Inspect holder contract, agency/subtier and parent"
            }),
            [
                [
                    'holderContractNumber',
                    'Holder contract PIID'
                ],
                [
                    'scope',
                    'Pool / domain / SIN scope'
                ],
                [
                    'sourceBasis',
                    'Source / basis'
                ],
                [
                    'sourceArtifactId',
                    'Source artifact ID'
                ],
                [
                    'verifiedFact',
                    'Verified fact'
                ],
                [
                    'notes',
                    'Notes'
                ]
            ].map(([key, label])=>/*#__PURE__*/ _jsx(SpecraFormField, {
                    label: label,
                    children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                        "aria-label": label,
                        value: draft[key],
                        required: key === 'sourceBasis',
                        onChange: (e)=>set({
                                [key]: e.target.value
                            })
                    })
                }, key)),
            [
                [
                    'holdingStart',
                    'Holding start'
                ],
                [
                    'holdingEnd',
                    'Holding end'
                ],
                [
                    'verificationStart',
                    'Verification start'
                ],
                [
                    'verificationEnd',
                    'Verification end'
                ]
            ].map(([key, label])=>/*#__PURE__*/ _jsx(SpecraFormField, {
                    label: label,
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        "aria-label": label,
                        type: "date",
                        value: dateInput(draft[key]),
                        onChange: (e)=>set({
                                [key]: dateValue(e.target.value)
                            })
                    })
                }, key)),
            /*#__PURE__*/ _jsx(SpecraFormField, {
                label: "Source verification",
                children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                    "aria-label": "Source verification",
                    value: draft.verificationStatus || 'UNVERIFIED',
                    onChange: (e)=>set({
                            verificationStatus: e.target.value
                        }),
                    children: [
                        /*#__PURE__*/ _jsx("option", {
                            value: "UNVERIFIED",
                            children: "Unverified"
                        }),
                        /*#__PURE__*/ _jsx("option", {
                            value: "VERIFIED",
                            children: "Verified fact from source"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Unspecified scope means unknown, never all pools. A parent or JV membership transfers no holding. Source verification is not procurement eligibility. Correct company/vehicle endpoints by archiving and recording a new relationship."
            }),
            (validation || writes.relationship.error) && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: validation || vehicleSaveError(writes.relationship.error)
            }),
            blocked && /*#__PURE__*/ _jsx("p", {
                children: "Close this editor and reload the relationship before reconciling; your draft is retained here."
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "submit",
                        "data-testid": Testids.vehicleIdentity.saveRelationship,
                        disabled: writes.relationship.isPending || blocked,
                        children: writes.relationship.isPending ? 'Saving…' : 'Save relationship'
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        variant: "secondary",
                        onClick: onDone,
                        children: "Cancel"
                    })
                ]
            })
        ]
    });
}
